import gsap from 'gsap';
import * as THREE from 'three';

function displacementSlider(opts) {
    const vertex = `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
    `;

    const fragment = `
        varying vec2 vUv;

        uniform sampler2D currentImage;
        uniform sampler2D nextImage;

        uniform float dispFactor;

        void main() {

            vec2 uv = vUv;
            vec4 _currentImage;
            vec4 _nextImage;
            float intensity = 0.3;

            vec4 orig1 = texture2D(currentImage, uv);
            vec4 orig2 = texture2D(nextImage, uv);

            _currentImage = texture2D(currentImage, vec2(uv.x, uv.y + dispFactor * (orig2 * intensity)));

            _nextImage = texture2D(nextImage, vec2(uv.x, uv.y + (1.0 - dispFactor) * (orig1 * intensity)));

            vec4 finalTexture = mix(_currentImage, _nextImage, dispFactor);

            gl_FragColor = finalTexture;

        }
    `;

    const images = opts.images;
    let image;
    const sliderImages = [];
    const canvasWidth = images[0].clientWidth;
    const canvasHeight = images[0].clientHeight;
    const parent = opts.parent;
    const renderWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const renderHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    let renderW;
    let renderH;

    if (renderWidth > canvasWidth) {
        renderW = renderWidth;
    } else {
        renderW = canvasWidth;
    }

    renderH = canvasHeight;

    const renderer = new THREE.WebGLRenderer({
        antialias: false
    });

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x23272A, 1.0);
    renderer.setSize(renderW, renderH);
    parent.appendChild(renderer.domElement);

    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';

    images.forEach((img) => {
        image = loader.load(img.getAttribute('src') + '?v=' + Date.now());
        image.magFilter = image.minFilter = THREE.LinearFilter;
        image.anisotropy = renderer.capabilities.getMaxAnisotropy();
        sliderImages.push(image);
    });

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x23272A);
    const camera = new THREE.OrthographicCamera(
        renderWidth / -2,
        renderWidth / 2,
        renderHeight / 2,
        renderHeight / -2,
        1,
        1000
    );

    camera.position.z = 1;

    const mat = new THREE.ShaderMaterial({
        uniforms: {
            dispFactor: { type: 'f', value: 0.0 },
            currentImage: { type: 't', value: sliderImages[0] },
            nextImage: { type: 't', value: sliderImages[1] }
        },
        vertexShader: vertex,
        fragmentShader: fragment,
        transparent: true,
        opacity: 1.0
    });

    const geometry = new THREE.PlaneGeometry(
        parent.offsetWidth,
        parent.offsetHeight,
        1,
        1
    );

    const object = new THREE.Mesh(geometry, mat);
    object.position.set(0, 0, 0);
    scene.add(object);

    const addEvents = function () {
        const pagButtons = Array.from(document.getElementById('pagination').querySelectorAll('button'));
        let isAnimating = false;

        pagButtons.forEach((el) => {
            el.addEventListener('click', function () {
                if (!isAnimating) {
                    isAnimating = true;

                    document.getElementById('pagination').querySelectorAll('.active')[0].className = '';
                    this.className = 'active';

                    const slideId = parseInt(this.dataset.slide, 10);

                    mat.uniforms.nextImage.value = sliderImages[slideId];
                    mat.uniforms.nextImage.needsUpdate = true;

                    gsap.to(mat.uniforms.dispFactor, {
                        duration: 1,
                        value: 1,
                        ease: 'expo.inOut',
                        onComplete: function () {
                            mat.uniforms.currentImage.value = sliderImages[slideId];
                            mat.uniforms.currentImage.needsUpdate = true;
                            mat.uniforms.dispFactor.value = 0.0;
                            isAnimating = false;
                        }
                    });

                    const slideTitleEl = document.getElementById('slide-title');
                    const slideStatusEl = document.getElementById('slide-status');
                    const nextSlideTitle = document.querySelector(`[data-slide-title="${slideId}"]`).innerHTML;
                    const nextSlideStatus = document.querySelector(`[data-slide-status="${slideId}"]`).innerHTML;

                    gsap.fromTo(slideTitleEl,
                        { opacity: 1, y: 0 },
                        {
                            duration: 0.5,
                            opacity: 0,
                            y: 20,
                            ease: 'expo.in',
                            onComplete: function () {
                                slideTitleEl.innerHTML = nextSlideTitle;
                                gsap.to(slideTitleEl, {
                                    duration: 0.5,
                                    opacity: 1,
                                    y: 0
                                });
                            }
                        });

                    gsap.fromTo(slideStatusEl,
                        { opacity: 1, y: 0 },
                        {
                            duration: 0.5,
                            opacity: 0,
                            y: 20,
                            ease: 'expo.in',
                            onComplete: function () {
                                slideStatusEl.innerHTML = nextSlideStatus;
                                gsap.to(slideStatusEl, {
                                    duration: 0.5,
                                    opacity: 1,
                                    y: 0,
                                    delay: 0.1
                                });
                            }
                        });
                }
            });
        });
    };

    addEvents();

    window.addEventListener('resize', function () {
        renderer.setSize(renderW, renderH);
    });

    const animate = function () {
        requestAnimationFrame(animate);
        renderer.render(scene, camera);
    };
    animate();
}

export function initSlider() {
    const pagination = document.getElementById('pagination');
    if (!pagination) {
        return;
    }
    const buttons = Array.from(pagination.querySelectorAll('button[data-slide]'));

    const titleEl = document.getElementById('slide-title');
    const statusEl = document.getElementById('slide-status');

    const slideContents = Array.from(document.querySelectorAll('[data-slide-content]'));

    function getTemplateHTML(selector) {
        const tpl = document.querySelector(selector);
        return tpl ? tpl.innerHTML : '';
    }

    function goTo(slideId) {
        buttons.forEach(b => {
            b.classList.toggle('active', b.dataset.slide === String(slideId));
        });

        const nextTitle = getTemplateHTML(`[data-slide-title="${slideId}"]`);
        const nextStatus = getTemplateHTML(`[data-slide-status="${slideId}"]`);

        titleEl.innerHTML = nextTitle;
        statusEl.innerHTML = nextStatus;

        if (slideContents.length) {
            slideContents.forEach(el => {
                el.hidden = el.dataset.slideContent !== String(slideId);
            });
        }
    }

    pagination.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-slide]');
        if (!btn) return;
        goTo(btn.dataset.slide);
    });

    if (buttons.length) {
        goTo(buttons.find(b => b.classList.contains('active'))?.dataset.slide ?? buttons[0].dataset.slide);
    }

    const images = document.querySelectorAll('#slider img');
    if (images.length > 1) {
        displacementSlider({ images, parent: document.getElementById('slider') });
    }
}
